import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../../context/ThemeContext';

const FeaturesSection = ({ language }) => {
    const fr = language === 'fr';
    const lang = fr ? 'fr' : 'en';
    const { theme } = useTheme();
    const themeStr = theme === 'dark' ? 'dark' : 'light';

    const content = {
        kicker: fr ? 'FONCTIONNALITÉS' : 'FEATURES',
        titleDim: fr ? 'Ce qui fait' : 'What makes',
        titleBright: fr ? ' la différence' : ' the difference',
        blocks: [
            {
                key: 'vu',
                icon: '/img/landing/features_icon_vu.svg',
                title: fr ? 'Vu à l’examen' : 'Seen on the exam',
                body: fr
                    ? 'Tu t’entraînes sur les vraies questions, celles qui tombent. Les dernières questions rencontrées par les candidats à l’examen de TC sont intégrées quotidiennement. Tu vois clairement sur quoi te concentrer.'
                    : 'You train on the real questions, the ones that actually show up on the TC exam. Updated continuously from the latest student reports, so you always know exactly where to focus.',
                image: `/img/landing/features_vu_${lang}_${themeStr}.webp`,
            },
            {
                key: 'motscles',
                icon: '/img/landing/features_icon_motscles.svg',
                title: fr ? 'Mots-clés' : 'Keywords',
                body: fr
                    ? 'Optimise ta préparation avec notre module exclusif « Mots-clés ». Ce mode génère un examen sur mesure, offrant ainsi une révision ultra-ciblée et un gain de temps précieux sur les points critiques de la formation.'
                    : 'Get more out of every study session with our exclusive Keywords module. It builds a custom exam around specific concepts, giving you ultra-targeted review on the points that decide your score.',
                image: `/img/landing/features_motscles_${lang}_${themeStr}.webp`,
            },
            {
                key: 'erreurs',
                icon: '/img/landing/features_icon_erreurs.svg',
                title: fr ? 'Cible tes erreurs' : 'Target your mistakes',
                body: fr
                    ? 'Travaille ce qui te bloque, pas ce que tu maîtrises déjà. Crée des examens sur mesure à partir de tes erreurs. Tu gagnes en maîtrise, là où ça compte vraiment.'
                    : 'Work on what’s giving you a hard time, not what you’ve already nailed. Build custom exams from your own wrong answers and gain ground where it really counts.',
                image: `/img/landing/features_erreurs_${lang}_${themeStr}.webp`,
            },
            {
                key: 'gamification',
                icon: '/img/landing/features_icon_gamification.svg',
                title: fr ? 'Garde ta motivation' : 'Stay motivated',
                body: fr
                    ? 'Débloque des badges au fur et à mesure de ton parcours. Chaque étape franchie est une victoire concrète.'
                    : 'Earn badges as you work through your training. Every milestone is a real win on the way to your licence.',
                image: `/img/landing/features_gamification_${lang}_${themeStr}.webp`,
            },
        ],
    };

    // === DESKTOP: Cardless pattern — text LEFT, image RIGHT (alternance vs PlatformSection) ===
    const [activeIndex, setActiveIndex] = useState(0);
    const blockRefs = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const idx = blockRefs.current.indexOf(entry.target);
                        if (idx !== -1) setActiveIndex(idx);
                    }
                });
            },
            { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
        );
        blockRefs.current.forEach((ref) => ref && observer.observe(ref));
        return () => observer.disconnect();
    }, []);

    // === MOBILE: horizontal carousel ===
    const [mobileActive, setMobileActive] = useState(0);
    const mobileScrollRef = useRef(null);
    const mobileCardRefs = useRef([]);

    useEffect(() => {
        const root = mobileScrollRef.current;
        if (!root) return;
        const observer = new IntersectionObserver(
            (entries) => {
                let best = null;
                entries.forEach((entry) => {
                    if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
                });
                if (best && best.isIntersecting) {
                    const idx = mobileCardRefs.current.indexOf(best.target);
                    if (idx !== -1) setMobileActive(idx);
                }
            },
            { root, threshold: [0.5, 0.75, 1] }
        );
        mobileCardRefs.current.forEach((ref) => ref && observer.observe(ref));
        return () => observer.disconnect();
    }, []);

    const scrollToMobileCard = (idx) => {
        const card = mobileCardRefs.current[idx];
        if (card) card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    };

    const renderHeader = (variant) => (
        <div>
            <p
                className={`font-['Rethink_Sans',sans-serif] font-bold text-[#71a7ff] ${
                    variant === 'mobile' ? 'text-[13px] tracking-[3px] leading-[24px]' : 'text-[14px] tracking-[3.5px] leading-[34px]'
                }`}
            >
                {content.kicker}
            </p>
            <h2
                className={`font-['Rethink_Sans',sans-serif] font-normal mt-3 ${
                    variant === 'mobile'
                        ? 'text-[40px] leading-[40px] tracking-[-1px]'
                        : 'text-[50px] leading-[46px] tracking-[-1.5px] mt-4'
                }`}
            >
                <span className="text-gray-400 dark:text-white/40">{content.titleDim}</span>
                <span className="text-gray-900 dark:text-white">{content.titleBright}</span>
            </h2>
        </div>
    );

    return (
        <section className="relative bg-white dark:bg-[#0f0f0f] text-gray-900 dark:text-white">
            {/* ================= MOBILE: horizontal carousel ================= */}
            <div className="md:hidden max-w-[1151px] mx-auto px-6 py-16">
                <div className="mb-10">{renderHeader('mobile')}</div>

                <div className="-mx-6">
                    <div
                        ref={mobileScrollRef}
                        className="flex overflow-x-auto snap-x snap-mandatory gap-4 px-6 pb-4 no-scrollbar"
                    >
                        {content.blocks.map((block, i) => (
                            <div
                                key={block.key}
                                ref={(el) => (mobileCardRefs.current[i] = el)}
                                className="snap-center shrink-0 w-[82%] max-w-[340px] flex flex-col gap-5"
                            >
                                <div className="aspect-[505/587] w-full rounded-[20px] overflow-hidden bg-gray-100 dark:bg-white/5">
                                    <img
                                        src={block.image}
                                        alt={block.title}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                </div>
                                <img src={block.icon} alt="" className="w-[52px] h-[52px]" />
                                <h3 className="font-['Rethink_Sans',sans-serif] font-semibold text-[20px] leading-[26px] text-gray-900 dark:text-white">
                                    {block.title}
                                </h3>
                                <p className="font-['Rethink_Sans',sans-serif] font-normal text-[15px] leading-[24px] text-gray-600 dark:text-[#a9b1bd]">
                                    {block.body}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-center gap-2 mt-4">
                        {content.blocks.map((block, i) => (
                            <button
                                key={block.key}
                                type="button"
                                aria-label={`Go to ${block.title}`}
                                onClick={() => scrollToMobileCard(i)}
                                className={`transition-all duration-300 rounded-full ${
                                    i === mobileActive ? 'w-6 h-2 bg-gray-900 dark:bg-white' : 'w-2 h-2 bg-gray-300 dark:bg-white/30 hover:bg-gray-400 dark:hover:bg-gray-100 dark:bg-white/50'
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* ================= DESKTOP: Cardless pattern — TEXT LEFT, IMAGE RIGHT (alternance) ================= */}
            <div className="hidden md:block max-w-[1151px] mx-auto px-6 py-24">
                <div className="grid grid-cols-[521px_505px] gap-[125px]">
                    {/* LEFT: header + 4 blocks (scrolls naturally) */}
                    <div>
                        <div>{renderHeader('desktop')}</div>

                        <div className="flex flex-col">
                            {content.blocks.map((block, i) => (
                                <div
                                    key={block.key}
                                    ref={(el) => (blockRefs.current[i] = el)}
                                    className={`flex flex-col gap-[27px] py-[140px] ${
                                        i > 0 ? 'border-t border-gray-200 dark:border-white/10' : ''
                                    }`}
                                >
                                    <img src={block.icon} alt="" className="w-[62px] h-[62px]" />
                                    <h3 className="font-['Rethink_Sans',sans-serif] font-semibold text-[25px] leading-[31px] text-gray-900 dark:text-white">
                                        {block.title}
                                    </h3>
                                    <p className="font-['Rethink_Sans',sans-serif] font-normal text-[20px] leading-[31px] text-gray-600 dark:text-[#a9b1bd]">
                                        {block.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT: sticky image stack with crossfade */}
                    <div>
                        <div style={{ position: 'sticky', top: 'calc(50vh - 293px)' }}>
                            <div className="relative w-[505px] h-[587px] rounded-[20px] overflow-hidden">
                                {content.blocks.map((block, i) => (
                                    <img
                                        key={block.key}
                                        src={block.image}
                                        alt={block.title}
                                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out"
                                        style={{ opacity: i === activeIndex ? 1 : 0 }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeaturesSection;
